/**
 * InsufficientCreditsModal — modale MUI globale ouverte sur chaque HTTP 402.
 *
 * Écoute l'évènement DOM `life:insufficient-credits` émis par
 * `utils/api.js` (pour éviter un import circulaire avec le store Redux).
 *
 * Charte cohérente avec Subscription : header navy #001B42, fond blanc,
 * dialog MUI, slide-up.
 */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'

import {
  closeInsufficient,
  openInsufficient,
  selectInsufficient,
  selectWallet,
} from '../../reduxLib/slices/billingSlice'
import CreditStore from './CreditStore'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function InsufficientCreditsModal() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const insufficient = useSelector(selectInsufficient)
  const wallet = useSelector(selectWallet)
  const [storeOpen, setStoreOpen] = useState(false)

  // Pont entre l'évènement DOM (axios → window.dispatchEvent) et le store.
  // Posé une fois au montage du composant.
  useEffect(() => {
    function onHttp402(e) {
      dispatch(openInsufficient(e.detail || null))
    }
    window.addEventListener('life:insufficient-credits', onHttp402)
    return () => window.removeEventListener('life:insufficient-credits', onHttp402)
  }, [dispatch])

  const open = !!insufficient

  function handleClose() {
    dispatch(closeInsufficient())
    setStoreOpen(false)
  }

  const isTierLocked = insufficient?.code === 'tier_not_available'

  return (
    <>
      <Dialog
        open={open && !storeOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px', overflow: 'hidden' } }}
      >
        <Box sx={{ background: '#001B42', px: 3, py: 2.5, display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ color: 'white', fontSize: 18, fontWeight: 600, flex: 1 }}>
            {isTierLocked ? 'Modèle premium verrouillé' : 'Crédits insuffisants'}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} aria-label="Fermer">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 4, textAlign: 'center', bgcolor: 'white' }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: '#001B42', opacity: 0.4 }} />

          {isTierLocked ? (
            <Typography sx={{ fontSize: 15, mt: 2, color: '#001B42' }}>
              Le modèle premium nécessite un abonnement Standard ou Premium.
              Passez à une offre payante pour y accéder.
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 15, color: '#001B42' }}>
                Cette action coûte{' '}
                <strong>{insufficient?.required ?? '?'} crédits</strong>.
                Vous en avez{' '}
                <strong>{insufficient?.available ?? wallet?.total ?? 0}</strong>.
              </Typography>
              {insufficient?.missing > 0 && (
                <Typography sx={{ fontSize: 13, color: 'gray', mt: 1 }}>
                  Il vous manque {insufficient.missing} crédit
                  {insufficient.missing > 1 ? 's' : ''} pour continuer.
                </Typography>
              )}
            </Box>
          )}

          <Box display="flex" flexDirection="column" gap={1.5} mt={3.5}>
            {!isTierLocked && (
              <Button
                variant="contained"
                onClick={() => setStoreOpen(true)}
                sx={{
                  bgcolor: '#001B42',
                  '&:hover': { bgcolor: '#002a5c' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Acheter un pack
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => {
                handleClose()
                navigate('/subscription')
              }}
              sx={{ textTransform: 'none' }}
            >
              {isTierLocked ? 'Voir les offres' : 'Passer à une offre supérieure'}
            </Button>
            <Button
              variant="text"
              onClick={handleClose}
              sx={{ textTransform: 'none', color: 'gray' }}
            >
              Annuler
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CreditStore open={storeOpen} onClose={handleClose} />
    </>
  )
}
